import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, CalendarClock, CheckCircle2, DollarSign, Pencil, Download, Receipt, FileText, Plus, Trash2, Save, IndianRupee } from "lucide-react";
import { exportRows, todayStamp } from "@/lib/csv";
import TechniciansPanel from "@/components/admin/TechniciansPanel";
import CaseLogsPanel from "@/components/admin/CaseLogsPanel";
import QuickRequestsPanel from "@/components/admin/QuickRequestsPanel";

const STATUSES = ["payment_pending", "scheduled", "in_progress", "completed", "escalated", "cancelled"];
const statusStyle = {
  payment_pending: "bg-amber-100 text-amber-800 border-amber-300",
  scheduled: "bg-blue-100 text-blue-700 border-blue-300",
  in_progress: "bg-blue-600 text-slate-900 border-blue-700",
  completed: "bg-slate-800 text-slate-900 border-slate-900",
  escalated: "bg-red-100 text-red-700 border-red-300",
  cancelled: "bg-slate-50 text-slate-500 border-slate-300",
};

function StatCard({ icon: Icon, label, value, tint, testid }) {
  return (
    <div data-testid={testid} className={`${tint} border border-slate-200 rounded-2xl p-5 soft-card`}>
      <Icon className="w-6 h-6 text-slate-900" strokeWidth={2.2} />
      <div className="font-display text-3xl font-extrabold mt-3">{value}</div>
      <div className="text-sm font-semibold text-slate-700 mt-1">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [s, ss, us] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/sessions${statusFilter !== "all" ? `?status_filter=${statusFilter}` : ""}`),
        api.get("/admin/users"),
      ]);
      setStats(s.data); setSessions(ss.data); setUsers(us.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const transactions = sessions
    .filter((s) => s.status === "completed")
    .map((s, i) => ({
      txn_id: `TXN-${String(i + 1).padStart(5, "0")}`,
      session_id: s.id,
      completed_at: s.updated_at || s.scheduled_at,
      user_name: s.user_name,
      user_email: s.user_email,
      category: s.category,
      technician: s.technician || "",
      duration_minutes: s.duration_minutes || 0,
      amount: s.price ?? 0,
      status: "PAID",
    }));

  const handleExportSessions = () => {
    exportRows(`yonsii-case-logs-${todayStamp()}.csv`, sessions, [
      { key: "id", label: "Session ID" },
      { key: "user_name", label: "User" },
      { key: "user_email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "category", label: "Category" },
      { key: "issue_description", label: "Issue description" },
      { key: "scheduled_at", label: "Scheduled at", format: (v) => v ? new Date(v).toLocaleString() : "" },
      { key: "status", label: "Status" },
      { key: "technician", label: "Technician" },
      { key: "duration_minutes", label: "Duration (min)" },
      { key: "resolution_notes", label: "Resolution notes" },
      { key: "price", label: "Price (USD)" },
      { key: "created_at", label: "Created at", format: (v) => v ? new Date(v).toLocaleString() : "" },
    ]);
    toast.success(`Exported ${sessions.length} case log${sessions.length === 1 ? "" : "s"}`);
  };

  const handleExportUsers = () => {
    exportRows(`yonsii-users-${todayStamp()}.csv`, users, [
      { key: "id", label: "User ID" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "total_sessions", label: "Total sessions" },
      { key: "total_spend", label: "Total spend (USD)", format: (v) => (v ?? 0).toFixed(2) },
      { key: "created_at", label: "Joined at", format: (v) => v ? new Date(v).toLocaleString() : "" },
    ]);
    toast.success(`Exported ${users.length} user${users.length === 1 ? "" : "s"}`);
  };

  const handleExportTransactions = () => {
    exportRows(`yonsii-transactions-${todayStamp()}.csv`, transactions, [
      { key: "txn_id", label: "Transaction ID" },
      { key: "session_id", label: "Session ID" },
      { key: "completed_at", label: "Completed at", format: (v) => v ? new Date(v).toLocaleString() : "" },
      { key: "user_name", label: "User" },
      { key: "user_email", label: "Email" },
      { key: "category", label: "Category" },
      { key: "technician", label: "Technician" },
      { key: "duration_minutes", label: "Duration (min)" },
      { key: "amount", label: "Amount (USD)", format: (v) => (v ?? 0).toFixed(2) },
      { key: "status", label: "Payment status" },
    ]);
    toast.success(`Exported ${transactions.length} transaction${transactions.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="chip-light mb-3">Admin · Command center</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">All sessions & users</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <StatCard testid="admin-stat-revenue" icon={DollarSign} tint="bg-blue-100" label="Total revenue" value={`₹${(stats?.total_revenue ?? 0).toFixed(0)}`} />
          <StatCard testid="admin-stat-scheduled" icon={CalendarClock} tint="bg-blue-100" label="Scheduled queue" value={stats?.scheduled ?? 0} />
          <StatCard testid="admin-stat-completed" icon={CheckCircle2} tint="bg-blue-100" label="Completed" value={stats?.completed ?? 0} />
          <StatCard testid="admin-stat-users" icon={Users} tint="bg-slate-200" label="Total users" value={stats?.total_users ?? 0} />
        </div>

        <Tabs defaultValue="sessions">
          <TabsList data-testid="admin-tabs" className="w-full max-w-full overflow-x-auto no-scrollbar justify-start flex-nowrap bg-slate-50 border border-slate-200 p-1 rounded-xl">
            <TabsTrigger value="sessions" data-testid="admin-tab-sessions" className="shrink-0">Sessions</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="shrink-0">Users</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="admin-tab-transactions" className="shrink-0">Transactions</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content" className="shrink-0">Content</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog" className="shrink-0">Blog</TabsTrigger>
            <TabsTrigger value="technicians" data-testid="admin-tab-technicians" className="shrink-0">Technicians</TabsTrigger>
            <TabsTrigger value="caselogs" data-testid="admin-tab-caselogs" className="shrink-0">Case logs</TabsTrigger>
            <TabsTrigger value="quickrequests" data-testid="admin-tab-quickrequests" className="shrink-0">Quick requests</TabsTrigger>
          </TabsList>

          <TabsContent value="sessions" className="mt-6">
            <div className="soft-card p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="font-display text-xl font-bold">Master sessions table</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-sm">Status:</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="admin-filter-status" className="w-40 border border-slate-200 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button
                    data-testid="admin-export-sessions-btn"
                    onClick={handleExportSessions}
                    disabled={sessions.length === 0}
                    className="rounded-full bg-brand hover:bg-brand-dark text-slate-900 font-semibold h-10 px-4 shadow-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Scheduled</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Technician</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow key={s.id} data-testid={`admin-session-row-${s.id}`}>
                        <TableCell>
                          <div className="font-semibold">{s.user_name}</div>
                          <div className="text-xs text-slate-500">{s.user_email}</div>
                        </TableCell>
                        <TableCell>{s.category}</TableCell>
                        <TableCell>{new Date(s.scheduled_at).toLocaleString()}</TableCell>
                        <TableCell className="font-mono text-sm">{s.phone}</TableCell>
                        <TableCell>{s.technician || <span className="text-slate-500">—</span>}</TableCell>
                        <TableCell>
                          {s.payment_status === "paid" ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border bg-emerald-100 text-emerald-800 border-emerald-300 w-fit">
                                Paid ₹{(s.advance_amount || 0).toFixed(0)}
                              </span>
                              {s.razorpay_payment_id && (
                                <span className="text-[10px] font-mono text-slate-500">{s.razorpay_payment_id}</span>
                              )}
                            </div>
                          ) : s.payment_status === "pending" ? (
                            <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border bg-amber-100 text-amber-800 border-amber-300">
                              Pending
                            </span>
                          ) : (
                            <span className="text-xs text-slate-500">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${statusStyle[s.status]}`}>{s.status}</span>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" className="border border-slate-200 font-semibold" data-testid={`admin-edit-btn-${s.id}`} onClick={() => setEditing(s)}>
                            <Pencil className="w-4 h-4 mr-1" /> Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {sessions.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No sessions found.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="soft-card p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="font-display text-xl font-bold">User directory</div>
                <Button
                  data-testid="admin-export-users-btn"
                  onClick={handleExportUsers}
                  disabled={users.length === 0}
                  className="rounded-full bg-brand hover:bg-brand-dark text-slate-900 font-semibold h-10 px-4 shadow-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Total sessions</TableHead>
                      <TableHead>Total spend</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                        <TableCell className="font-semibold">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell className="font-mono text-sm">{u.phone || "—"}</TableCell>
                        <TableCell>{u.total_sessions}</TableCell>
                        <TableCell>₹{u.total_spend?.toFixed(0) ?? 0}</TableCell>
                        <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">No users yet.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="transactions" className="mt-6">
            <div className="soft-card p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <div className="font-display text-xl font-bold flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-blue-600" /> Payment transactions
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {transactions.length} paid session{transactions.length === 1 ? "" : "s"} · Total ₹{transactions.reduce((a, t) => a + (t.amount || 0), 0).toFixed(0)}
                  </div>
                </div>
                <Button
                  data-testid="admin-export-transactions-btn"
                  onClick={handleExportTransactions}
                  disabled={transactions.length === 0}
                  className="rounded-full bg-brand hover:bg-brand-dark text-slate-900 font-semibold h-10 px-4 shadow-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Txn ID</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Technician</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((t) => (
                      <TableRow key={t.session_id} data-testid={`admin-txn-row-${t.session_id}`}>
                        <TableCell className="font-mono text-xs text-slate-700">{t.txn_id}</TableCell>
                        <TableCell>{new Date(t.completed_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="font-semibold">{t.user_name}</div>
                          <div className="text-xs text-slate-500">{t.user_email}</div>
                        </TableCell>
                        <TableCell>{t.category}</TableCell>
                        <TableCell>{t.technician || <span className="text-slate-500">—</span>}</TableCell>
                        <TableCell>{t.duration_minutes} min</TableCell>
                        <TableCell className="font-semibold">₹{t.amount.toFixed(0)}</TableCell>
                        <TableCell>
                          <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border bg-blue-100 text-blue-700 border-blue-300">
                            {t.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {transactions.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No completed sessions yet.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-6">
            <ContentEditor />
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            <BlogEditor />
          </TabsContent>

          <TabsContent value="technicians" className="mt-6">
            <TechniciansPanel />
          </TabsContent>

          <TabsContent value="caselogs" className="mt-6">
            <CaseLogsPanel />
          </TabsContent>

          <TabsContent value="quickrequests" className="mt-6">
            <QuickRequestsPanel />
          </TabsContent>
        </Tabs>
      </div>

      <SessionEditor session={editing} onClose={() => { setEditing(null); load(); }} />
    </div>
  );
}

function SessionEditor({ session, onClose }) {
  const [form, setForm] = useState({ status: "", technician_id: "none", duration_minutes: 0, resolution_notes: "", price: "" });
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    api.get("/admin/technicians").then((r) => setTechs(r.data)).catch(() => {});
  }, [session]);

  useEffect(() => {
    if (session) {
      setForm({
        status: session.status || "scheduled",
        technician_id: session.technician_id || "none",
        duration_minutes: session.duration_minutes || 0,
        resolution_notes: session.resolution_notes || "",
        price: session.price == null ? "" : String(session.price),
      });
    }
  }, [session]);

  if (!session) return null;

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        status: form.status,
        duration_minutes: parseInt(form.duration_minutes, 10) || 0,
        resolution_notes: form.resolution_notes,
      };
      if (form.technician_id !== (session.technician_id || "none")) {
        payload.technician_id = form.technician_id === "none" ? "" : form.technician_id;
      }
      const trimmedPrice = form.price.trim();
      if (trimmedPrice !== "") {
        const parsed = parseFloat(trimmedPrice);
        if (Number.isNaN(parsed) || parsed < 0) {
          toast.error("Price must be a non-negative number");
          setLoading(false);
          return;
        }
        payload.price = parsed;
      }
      await api.patch(`/admin/sessions/${session.id}`, payload);
      toast.success("Session updated");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!session} onOpenChange={(o) => !o && onClose()}>      <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Manage session</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <div><span className="font-semibold">{session.user_name}</span> · {session.user_email}</div>
            <div className="text-slate-500 mt-1">{session.category} · {new Date(session.scheduled_at).toLocaleString()}</div>
            <div className="text-slate-500 mt-1">Issue: {session.issue_description}</div>
          </div>
          <div>
            <Label className="font-semibold">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="admin-session-status-select" className="mt-1 border border-slate-200 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-semibold">Assign technician</Label>
            <Select value={form.technician_id} onValueChange={(v) => setForm({ ...form, technician_id: v })}>
              <SelectTrigger data-testid="admin-assign-tech-input" className="mt-1 border border-slate-200 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{session.technician && !session.technician_id ? `${session.technician} (not linked)` : "Unassigned"}</SelectItem>
                {techs.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`assign-tech-option-${t.id}`}>
                    {t.name}{t.specialisation ? ` · ${t.specialisation}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-semibold">Duration (minutes)</Label>
            <Input data-testid="admin-duration-input" type="number" min={0} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="mt-1 border border-slate-200 h-11" />
          </div>
          <div>
            <Label className="font-semibold">Session price (INR)</Label>
            <Input data-testid="admin-price-input" type="number" min={0} step="0.01" placeholder={session.price == null ? "TBD — set a price" : ""} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 border border-slate-200 h-11" />
            {session.price == null && form.price.trim() === "" && (
              <p className="text-xs text-slate-500 mt-1">This session was booked with a placeholder price. Enter a final amount before marking it completed.</p>
            )}
          </div>
          <div>
            <Label className="font-semibold">Resolution notes</Label>
            <Textarea data-testid="admin-resolution-input" rows={4} value={form.resolution_notes} onChange={(e) => setForm({ ...form, resolution_notes: e.target.value })} className="mt-1 border border-slate-200" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={loading} data-testid="admin-save-resolution-btn" className="bg-brand hover:bg-brand-dark border border-slate-200 shadow-btn font-bold">
            {loading ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------------------- Content Management --------------------
function ContentEditor() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/content");
      setContent(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !content) {
    return <div className="soft-card p-8 text-center text-slate-500">Loading content…</div>;
  }

  const patch = (partial) => setContent((c) => ({ ...c, ...partial }));
  const patchHero = (partial) => patch({ hero: { ...content.hero, ...partial } });
  const patchAbout = (partial) => patch({ about: { ...content.about, ...partial } });
  const patchContact = (partial) => patch({ contact: { ...content.contact, ...partial } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hero: content.hero,
        services: content.services,
        testimonials: content.testimonials,
        about: content.about,
        contact: content.contact,
      };
      const { data } = await api.patch("/admin/content", payload);
      setContent(data);
      toast.success("Content saved · site updated live");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const addService = () => patch({ services: [...content.services, { title: "New service", description: "Describe the service", price_label: "TBD" }] });
  const rmService = (i) => patch({ services: content.services.filter((_, k) => k !== i) });
  const setService = (i, field, val) => patch({ services: content.services.map((s, k) => k === i ? { ...s, [field]: val } : s) });

  const addTestimonial = () => patch({ testimonials: [...content.testimonials, { name: "Name", city: "City", quote: "Their story here." }] });
  const rmTestimonial = (i) => patch({ testimonials: content.testimonials.filter((_, k) => k !== i) });
  const setTestimonial = (i, field, val) => patch({ testimonials: content.testimonials.map((t, k) => k === i ? { ...t, [field]: val } : t) });

  const values = content.about?.values || [];
  const setValue = (i, field, val) => patchAbout({ values: values.map((v, k) => k === i ? { ...v, [field]: val } : v) });
  const addValue = () => patchAbout({ values: [...values, { title: "New value", detail: "Detail here" }] });
  const rmValue = (i) => patchAbout({ values: values.filter((_, k) => k !== i) });

  return (
    <div className="space-y-6" data-testid="content-editor">
      <div className="soft-card p-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" /> Live site content
          </div>
          <div className="text-sm text-slate-500 mt-1">Edit hero copy, services, testimonials, about page and contact details. Saves publish immediately.</div>
        </div>
        <Button data-testid="content-save-btn" onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold h-11 px-5 shadow-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save all changes"}
        </Button>
      </div>

      {/* HERO */}
      <div className="soft-card p-6 space-y-4" data-testid="content-hero-section">
        <div className="font-display text-lg font-bold">Landing hero</div>
        <Field label="Trust note (top pill)"><Input data-testid="content-hero-trust" value={content.hero.trust_note || ""} onChange={(e) => patchHero({ trust_note: e.target.value })} /></Field>
        <Field label="Title"><Input data-testid="content-hero-title" value={content.hero.title || ""} onChange={(e) => patchHero({ title: e.target.value })} /></Field>
        <Field label="Subtitle"><Textarea data-testid="content-hero-subtitle" rows={3} value={content.hero.subtitle || ""} onChange={(e) => patchHero({ subtitle: e.target.value })} /></Field>
        <Field label="Primary CTA label"><Input data-testid="content-hero-cta" value={content.hero.primary_cta || ""} onChange={(e) => patchHero({ primary_cta: e.target.value })} /></Field>
      </div>

      {/* CATEGORY PRICING (quick per-row price edit) */}
      <CategoryPricingPanel
        services={content.services}
        onSaved={(next) => setContent({ ...content, services: next })}
      />

      {/* SERVICES */}
      <div className="soft-card p-6 space-y-4" data-testid="content-services-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-display text-lg font-bold">Services ({content.services.length})</div>
          <Button data-testid="content-service-add" size="sm" variant="outline" onClick={addService} className="border border-slate-200 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Add service
          </Button>
        </div>
        <div className="space-y-3">
          {content.services.map((s, i) => (
            <div key={i} data-testid={`content-service-${i}`} className="border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
              <div className="md:col-span-4">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Title</Label>
                <Input data-testid={`content-service-${i}-title`} value={s.title || ""} onChange={(e) => setService(i, "title", e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-6">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Description</Label>
                <Input data-testid={`content-service-${i}-desc`} value={s.description || ""} onChange={(e) => setService(i, "description", e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-1">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Price</Label>
                <Input data-testid={`content-service-${i}-price`} value={s.price_label || ""} onChange={(e) => setService(i, "price_label", e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-1 flex md:justify-end md:pt-6">
                <Button data-testid={`content-service-${i}-remove`} size="sm" variant="ghost" onClick={() => rmService(i)} className="text-slate-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* TESTIMONIALS */}
      <div className="soft-card p-6 space-y-4" data-testid="content-testimonials-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-display text-lg font-bold">Testimonials ({content.testimonials.length})</div>
          <Button data-testid="content-testimonial-add" size="sm" variant="outline" onClick={addTestimonial} className="border border-slate-200 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Add testimonial
          </Button>
        </div>
        <div className="space-y-3">
          {content.testimonials.map((t, i) => (
            <div key={i} data-testid={`content-testimonial-${i}`} className="border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
              <div className="md:col-span-3">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Name</Label>
                <Input data-testid={`content-testimonial-${i}-name`} value={t.name || ""} onChange={(e) => setTestimonial(i, "name", e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">City</Label>
                <Input data-testid={`content-testimonial-${i}-city`} value={t.city || ""} onChange={(e) => setTestimonial(i, "city", e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-6">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Quote</Label>
                <Input data-testid={`content-testimonial-${i}-quote`} value={t.quote || ""} onChange={(e) => setTestimonial(i, "quote", e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-1 flex md:justify-end md:pt-6">
                <Button data-testid={`content-testimonial-${i}-remove`} size="sm" variant="ghost" onClick={() => rmTestimonial(i)} className="text-slate-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ABOUT */}
      <div className="soft-card p-6 space-y-4" data-testid="content-about-section">
        <div className="font-display text-lg font-bold">About page</div>
        <Field label="Heading"><Input data-testid="content-about-heading" value={content.about?.heading || ""} onChange={(e) => patchAbout({ heading: e.target.value })} /></Field>
        <Field label="Mission (one-liner)"><Textarea data-testid="content-about-mission" rows={2} value={content.about?.mission || ""} onChange={(e) => patchAbout({ mission: e.target.value })} /></Field>
        <Field label="Body"><Textarea data-testid="content-about-body" rows={5} value={content.about?.body || ""} onChange={(e) => patchAbout({ body: e.target.value })} /></Field>
        <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
          <div className="text-sm font-semibold text-slate-700">Values ({values.length})</div>
          <Button data-testid="content-value-add" size="sm" variant="outline" onClick={addValue} className="border border-slate-200 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Add value
          </Button>
        </div>
        <div className="space-y-3">
          {values.map((v, i) => (
            <div key={i} data-testid={`content-value-${i}`} className="border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
              <div className="md:col-span-4">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Title</Label>
                <Input data-testid={`content-value-${i}-title`} value={v.title || ""} onChange={(e) => setValue(i, "title", e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-7">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Detail</Label>
                <Input data-testid={`content-value-${i}-detail`} value={v.detail || ""} onChange={(e) => setValue(i, "detail", e.target.value)} className="mt-1" />
              </div>
              <div className="md:col-span-1 flex md:justify-end md:pt-6">
                <Button data-testid={`content-value-${i}-remove`} size="sm" variant="ghost" onClick={() => rmValue(i)} className="text-slate-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CONTACT */}
      <div className="soft-card p-6 space-y-4" data-testid="content-contact-section">
        <div className="font-display text-lg font-bold">Contact details</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Email"><Input data-testid="content-contact-email" value={content.contact?.email || ""} onChange={(e) => patchContact({ email: e.target.value })} /></Field>
          <Field label="WhatsApp"><Input data-testid="content-contact-whatsapp" value={content.contact?.whatsapp || ""} onChange={(e) => patchContact({ whatsapp: e.target.value })} /></Field>
          <Field label="Phone"><Input data-testid="content-contact-phone" value={content.contact?.phone || ""} onChange={(e) => patchContact({ phone: e.target.value })} /></Field>
          <Field label="Service area"><Input data-testid="content-contact-service-area" value={content.contact?.service_area || ""} onChange={(e) => patchContact({ service_area: e.target.value })} /></Field>
          <Field label="Hours"><Input data-testid="content-contact-hours" value={content.contact?.hours || ""} onChange={(e) => patchContact({ hours: e.target.value })} /></Field>
        </div>
      </div>

      <div className="soft-card p-6 flex items-center justify-end">
        <Button data-testid="content-save-btn-bottom" onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold h-11 px-5 shadow-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save all changes"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// -------------------- Blog Management --------------------
function BlogEditor() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list mode, {} = new, object = edit
  const [plagCheck, setPlagCheck] = useState(null); // {loading, result} or null
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ title: "", category: "Tips", excerpt: "", content: "", author: "Yonsii Team", published: true });
  const cancel = () => { setEditing(null); setPlagCheck(null); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await api.patch(`/admin/blog/${editing.id}`, {
          title: editing.title,
          category: editing.category,
          excerpt: editing.excerpt,
          content: editing.content,
          author: editing.author,
          published: editing.published,
        });
        toast.success("Article updated");
      } else {
        await api.post("/admin/blog", editing);
        toast.success("Article published");
      }
      setEditing(null); setPlagCheck(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Article deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runPlagiarism = async () => {
    if (!editing?.content || editing.content.trim().length < 20) {
      toast.error("Add at least 20 characters of content first");
      return;
    }
    setPlagCheck({ loading: true });
    try {
      const { data } = await api.post("/admin/blog/plagiarism-check", {
        title: editing.title,
        content: editing.content,
      });
      setPlagCheck({ loading: false, result: data });
    } catch (e) {
      setPlagCheck(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Plagiarism check failed");
    }
  };

  // ---- List view ----
  if (!editing) {
    return (
      <div className="space-y-6" data-testid="blog-editor">
        <div className="soft-card p-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-xl font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> Blog articles
            </div>
            <div className="text-sm text-slate-500 mt-1">Create, edit, publish or hide tech-tip articles for the public /blog page.</div>
          </div>
          <Button data-testid="blog-new-btn" onClick={startNew} className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold h-11 px-5 shadow-btn">
            <Plus className="w-4 h-4 mr-2" /> New article
          </Button>
        </div>
        <div className="soft-card p-6">
          {loading ? (
            <div className="text-slate-500 text-center py-10">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="text-slate-500 text-center py-10">No articles yet — click <strong>New article</strong> to publish your first tip.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((p) => (
                  <TableRow key={p.id} data-testid={`blog-row-${p.slug}`}>
                    <TableCell>
                      <div className="font-semibold">{p.title}</div>
                      <div className="text-xs text-slate-500">/{p.slug}</div>
                    </TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${p.published ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                        {p.published ? "Published" : "Draft"}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{new Date(p.updated_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`blog-edit-${p.slug}`} className="border border-slate-200 font-semibold">
                          <Pencil className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`blog-delete-${p.slug}`} className="text-slate-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    );
  }

  // ---- Edit view ----
  const r = plagCheck?.result;
  return (
    <div className="space-y-6" data-testid="blog-editor-form">
      <div className="soft-card p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-display text-xl font-bold">{editing.id ? "Edit article" : "New article"}</div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={cancel} data-testid="blog-cancel-btn" className="text-slate-500 font-semibold">Cancel</Button>
            <Button
              data-testid="blog-plagiarism-btn"
              variant="outline"
              onClick={runPlagiarism}
              disabled={plagCheck?.loading}
              className="border border-slate-200 font-semibold rounded-full h-10 px-4"
            >
              {plagCheck?.loading ? "Checking…" : "Check plagiarism"}
            </Button>
            <Button
              data-testid="blog-save-btn"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold h-10 px-5 shadow-btn"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : editing.published ? "Publish" : "Save draft"}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-4">
            <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Title</Label>
            <Input data-testid="blog-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Category</Label>
            <Input data-testid="blog-category-input" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Excerpt (shown on the blog index)</Label>
          <Textarea data-testid="blog-excerpt-input" rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Article content</Label>
          <Textarea data-testid="blog-content-input" rows={14} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="mt-1 font-mono text-sm" />
        </div>
        <div className="flex items-center gap-3">
          <input
            id="blog-published"
            data-testid="blog-published-toggle"
            type="checkbox"
            checked={!!editing.published}
            onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
            className="w-4 h-4"
          />
          <Label htmlFor="blog-published" className="font-semibold cursor-pointer">Publish immediately (visible on public /blog)</Label>
        </div>
      </div>

      {plagCheck?.result && (
        <div data-testid="blog-plagiarism-result" className="soft-card p-6 space-y-3">
          <div className="font-display text-lg font-bold flex items-center gap-3">
            Plagiarism check
            <span data-testid="plagiarism-risk" className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${
              r.risk === "low" ? "bg-blue-100 text-blue-700 border-blue-300" :
              r.risk === "high" ? "bg-red-100 text-red-700 border-red-300" :
              "bg-amber-100 text-amber-700 border-amber-300"
            }`}>{r.risk} risk</span>
          </div>
          <div className="text-sm text-slate-600">Originality score: <strong data-testid="plagiarism-score">{r.originality_score}/100</strong></div>
          {r.flagged_phrases?.length > 0 && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Flagged phrases</div>
              <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                {r.flagged_phrases.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}
          {r.suggestions?.length > 0 && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Rewrite suggestions</div>
              <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                {r.suggestions.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// -------------------- Category Pricing Panel --------------------
function CategoryPricingPanel({ services, onSaved }) {
  const [editingIndex, setEditingIndex] = useState(-1);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (i) => { setEditingIndex(i); setDraft(services[i].price_label || ""); };
  const cancel = () => { setEditingIndex(-1); setDraft(""); };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed) { toast.error("Price cannot be empty"); return; }
    setSaving(true);
    try {
      const nextServices = services.map((s, k) => k === editingIndex ? { ...s, price_label: trimmed } : s);
      const { data } = await api.patch("/admin/content", { services: nextServices });
      onSaved(data.services);
      toast.success(`Price updated for ${services[editingIndex].title}`);
      setEditingIndex(-1);
      setDraft("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="soft-card p-6 space-y-4" data-testid="category-pricing-panel">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center">
          <IndianRupee className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-lg font-bold">Category pricing</div>
          <div className="text-sm text-slate-500">Quick edit per service — click <strong>Edit</strong>, type the new price, then <strong>Save</strong>. Changes persist and refresh on the public site on next load.</div>
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        {services.map((s, i) => {
          const isEditing = editingIndex === i;
          return (
            <div key={`${s.title}-${i}`} data-testid={`pricing-row-${i}`} className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${i > 0 ? "border-t border-slate-100" : ""}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold text-slate-900 truncate" data-testid={`pricing-row-${i}-title`}>{s.title}</div>
                {!isEditing && (
                  <div className="text-xs text-blue-700 font-bold mt-0.5" data-testid={`pricing-row-${i}-price`}>{s.price_label || "—"}</div>
                )}
              </div>
              {isEditing ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    data-testid={`pricing-row-${i}-input`}
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
                    className="h-9 w-56 border border-slate-200"
                    placeholder="e.g. ₹599"
                  />
                  <Button
                    data-testid={`pricing-row-${i}-save`}
                    size="sm"
                    disabled={saving}
                    onClick={save}
                    className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold h-9 px-4"
                  >
                    <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    data-testid={`pricing-row-${i}-cancel`}
                    size="sm"
                    variant="ghost"
                    onClick={cancel}
                    className="text-slate-500 font-semibold h-9"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid={`pricing-row-${i}-edit`}
                  size="sm"
                  variant="outline"
                  onClick={() => startEdit(i)}
                  className="border border-slate-200 font-semibold rounded-full h-9 px-4 text-slate-900 hover:bg-slate-100"
                >
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

